import { useNavigate } from 'react-router-dom';

import { StatusChip, type ChipTone } from '../../../components/ui/StatusChip';
import type { ChamberStatus } from '../../../types/dashboard';

const STATUS_LABEL: Record<ChamberStatus['status'], string> = {
  free: 'Вільна',
  loading: 'Завантаження',
  drying: 'Сушіння',
  unloading: 'Вивантаження',
};

const STATUS_TONE: Record<ChamberStatus['status'], ChipTone> = {
  free: 'neutral',
  loading: 'info',
  drying: 'brand',
  unloading: 'warning',
};

const DAY_MONTH: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit' };

function shortDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('uk-UA', DAY_MONTH) : '—';
}

function isOverdue(c: ChamberStatus): boolean {
  return c.days_total !== null && c.days_elapsed !== null && c.days_elapsed > c.days_total;
}

/** Рядок під обсягом: скільки днів іде сушіння або скільки прострочено. */
function miniLine(c: ChamberStatus): string {
  if (c.status === 'free') return 'очікує завантаження';
  if (isOverdue(c)) return `прострочено ${c.days_elapsed! - c.days_total!} дн`;
  if (c.days_elapsed !== null && c.days_total !== null)
    return `день ${c.days_elapsed} з ${c.days_total}`;
  return STATUS_LABEL[c.status].toLowerCase();
}

export function ChambersGrid({ chambers }: { chambers: ChamberStatus[] }) {
  const navigate = useNavigate();
  const busy = chambers.filter((c) => c.status !== 'free').length;

  return (
    <div className="dash-card" style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="dash-card-head">
        <span className="dash-section-label">Сушильні камери · {chambers.length}</span>
        <span className="dash-link" onClick={() => navigate('/drying')}>
          Відкрити сушіння →
        </span>
      </div>

      <div className="dash-chambers">
        {chambers.map((c) => {
          const free = c.status === 'free';
          const overdue = isOverdue(c);
          const barColor = overdue
            ? 'var(--error)'
            : c.status === 'unloading'
              ? 'var(--warning)'
              : 'var(--brand)';
          const cls = [
            'dash-chamber',
            free ? 'is-free' : 'is-busy',
            overdue ? 'is-overdue' : '',
          ].join(' ');

          return (
            <div
              key={c.id}
              className={cls}
              onClick={free ? undefined : () => navigate('/drying')}
              title={`Зайнято ${busy} з ${chambers.length} камер`}
            >
              <div className="dash-chamber-top">
                <span className="dash-chamber-num">№ {c.number}</span>
                <StatusChip tone={STATUS_TONE[c.status]} size="sm">
                  {STATUS_LABEL[c.status]}
                </StatusChip>
              </div>

              <div
                className="dash-chamber-vol"
                style={{ color: free ? 'var(--text-faint)' : 'var(--text)' }}
              >
                {free
                  ? '—'
                  : parseFloat(c.current_volume_m3).toLocaleString('uk-UA', {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}
                <span className="dash-chamber-cap"> / {parseFloat(c.capacity_m3)} м³</span>
              </div>

              <div className="dash-bar">
                <span
                  style={{
                    width: `${free ? 0 : (c.progress_pct ?? c.occupancy_pct)}%`,
                    background: barColor,
                  }}
                />
              </div>

              <div className={`dash-chamber-mini${overdue ? ' is-overdue' : ''}`}>
                {miniLine(c)}
              </div>

              <div className="dash-chamber-foot">
                <div className="dash-chamber-mat">
                  {c.materials.length > 0 ? c.materials.join(' · ') : 'Порожня'}
                </div>
                <div className="dash-chamber-dates">
                  {free
                    ? `вільно ${parseFloat(c.capacity_m3)} м³`
                    : `${shortDate(c.start_date)} → ${shortDate(c.forecast_end_date)}`}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
